import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { apiClient } from '@services/api'
import { invoiceMessage, waLink } from '../utils/whatsapp'
import { WhatsAppIcon } from '../components/WhatsAppIcon'

type Mode = 'Cash' | 'UPI' | 'Net Banking' | 'Cheque'
type Paper = 'A4' | 'A5'
const COPY_LABELS = ['ORIGINAL', 'DUPLICATE', 'TRIPLICATE'] as const

interface Allocation { batchNo: string; packets: number; expiryDate: string; unitCost: number }
interface Line { id: number; product: string; nameTamil: string | null; packG: number; unit: string; unitLabel: string; label: string; quantity: number; rate: number; gstRate: number; amount: number; taxAmount: number; costAmount: number; allocations: Allocation[] }
interface Collection { id: number; receiptNo: string; amount: number; date: string; mode: Mode; referenceNo: string | null; recordedBy: string; editedBy?: string | null }
interface Invoice {
  id: number
  invoiceNo: string
  invoiceDate: string
  dueDate: string | null
  customerName: string
  customerPhone: string | null
  channel: string
  lineItems: Line[]
  collections: Collection[]
  subTotal: number
  discountMode?: 'percent' | 'amount'
  discountValue?: number
  discountAmount?: number
  taxableAmount: number
  gstAmount: number
  cgst: number
  sgst: number
  roundOff: number
  amount: number
  amountPaid: number
  balance: number
  amountInWords: string
  type: 'Cash' | 'Credit' | 'UPI'
  paymentStatus: 'Paid' | 'Partial' | 'Unpaid' | 'Cancelled' | 'Draft'
  overdueDays: number
  createdBy: string
  isWalkin: boolean
  editable?: boolean
  store: { id: number; name: string; phone?: string | null; location?: string | null; address?: string | null; gstin?: string | null; route?: string | null; reliabilityGrade: string; onTimePct: number | null; outstanding: number; overdue: number } | null
  company: {
    name: string; address?: string | null; city?: string | null; state?: string | null; pincode?: string | null; country?: string | null; gstin?: string | null; phone?: string | null; logoUrl?: string | null
    invoiceNotes?: string | null; invoiceTerms?: string | null
    bankName?: string | null; bankBranch?: string | null; accountName?: string | null; accountNo?: string | null; ifsc?: string | null; upiId?: string | null; whatsappNumber?: string | null
  } | null
}

const today = () => new Date().toISOString().split('T')[0]
const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const money = (n: number | null | undefined) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const dmy = (iso: string) => { const [y, m, d] = iso.split('-'); return `${d}-${m}-${y}` }
const statusBadge: Record<string, string> = { Paid: 'badge-full', Partial: 'badge-partial', Unpaid: 'badge-unpaid', Cancelled: 'badge-draft', Draft: 'badge-draft' }

/** One printable copy of the invoice, laid out like the company's SE-series tax invoice. */
function InvoiceSheet({ inv, copy, paper }: { inv: Invoice; copy: string; paper: Paper }) {
  const co = inv.company
  const upiLink = co?.upiId ? `upi://pay?pa=${encodeURIComponent(co.upiId)}&pn=${encodeURIComponent(co.accountName || co.name)}&am=${inv.amount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(inv.invoiceNo)}` : ''
  const hasBank = !!(co?.bankName || co?.accountNo || co?.upiId)
  return (
    <div className={`inv-sheet inv-copy paper-${paper.toLowerCase()}`} data-copy={copy}>
      <div className="inv-copy-tag">{copy}</div>
      <div className="inv-head">
        <div className="inv-co">
          {co?.logoUrl && <img src={co.logoUrl} alt="" className="inv-logo" />}
          <div>
            <div className="inv-co-name">{co?.name}</div>
            {co?.address && (() => { const parts = co.address.split(',').map((p) => p.trim()).filter(Boolean); const lines = parts.length > 3 ? [parts.slice(0, 3).join(', '), parts.slice(3).join(', ')] : [parts.join(', ')]; return lines.map((l, i) => <div key={i}>{l}</div>) })()}
            {(co?.city || co?.pincode) && <div>{co?.city}{co?.pincode ? `- ${co.pincode}` : ''}</div>}
            {co?.state && <div>{co.state}</div>}
            <div>{co?.country || 'India'}</div>
            {co?.gstin && <div>GSTIN: {co.gstin}</div>}
            {co?.phone && <div>Ph: {co.phone}</div>}
          </div>
        </div>
        <div className="inv-title">
          <div className="inv-title-main">Tax Invoice</div>
          <div className="inv-title-no">Invoice # {inv.invoiceNo}</div>
          <div className="inv-title-status">{inv.paymentStatus}</div>
        </div>
      </div>

      <table className="inv-box">
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <div className="inv-kv"><span>Date</span><span>: {dmy(inv.invoiceDate)}</span></div>
              <div className="inv-kv"><span>Channel</span><span>: {inv.channel}</span></div>
            </td>
            <td>
              <div>Bill To :</div>
              <div className="inv-bill-name">{inv.customerName}</div>
              {(inv.store?.address || inv.store?.location) && <div>{inv.store?.address || inv.store?.location}</div>}
              <div>{co?.country || 'India'}</div>
              {inv.customerPhone && <div>Ph: {inv.customerPhone}</div>}
              {inv.store?.gstin && <div>GSTIN: {inv.store.gstin}</div>}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="inv-items">
        <thead>
          <tr><th className="c-idx">#</th><th>Item</th><th className="num c-qty">Qty</th><th className="c-unit">Units</th><th className="num c-rate">Rate</th><th className="num c-gst">GST%</th><th className="num c-amt">Amount</th></tr>
        </thead>
        <tbody>
          {inv.lineItems.map((l, i) => (
            <tr key={l.id}>
              <td className="c-idx">{i + 1}</td>
              <td>
                <div>{l.label}</div>
                {l.nameTamil && <div className="inv-tamil">{l.nameTamil}{l.packG > 0 ? ` - ${l.packG >= 1000 ? `${l.packG / 1000}kg` : `${l.packG}g`}` : ''}</div>}
              </td>
              <td className="num">{(Number(l.quantity) || 0).toFixed(2)}</td>
              <td className="c-unit">{l.unitLabel}</td>
              <td className="num">{money(l.rate)}</td>
              <td className="num">{l.gstRate}%</td>
              <td className="num">{money(Math.round((Number(l.quantity) || 0) * l.rate * 100) / 100)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="inv-totals-wrap">
        <table className="inv-totals">
          <tbody>
            <tr><td>Sub Total (Tax Inclusive)</td><td className="num">{money(inv.subTotal)}</td></tr>
            {(inv.discountAmount || 0) > 0 && <tr><td>Discount{inv.discountMode === 'percent' && inv.discountValue ? ` (${inv.discountValue}%)` : ''}</td><td className="num">-{money(inv.discountAmount || 0)}</td></tr>}
            <tr><td>CGST</td><td className="num">{money(inv.cgst)}</td></tr>
            <tr><td>SGST</td><td className="num">{money(inv.sgst)}</td></tr>
            <tr><td>Round Off</td><td className="num">{inv.roundOff < 0 ? '-' : ''}{money(Math.abs(inv.roundOff))}</td></tr>
            <tr className="inv-grand"><td>Total</td><td className="num">₹{money(inv.amount)}</td></tr>
          </tbody>
        </table>
      </div>

      <table className="inv-foot">
        <tbody>
          <tr><td><span className="inv-lbl">Total In Words :</span> {inv.amountInWords}</td></tr>
          {co?.invoiceNotes && <tr><td><span className="inv-lbl">Notes :</span> {co.invoiceNotes}</td></tr>}
          {co?.invoiceTerms && (
            <tr>
              <td className="inv-terms">
                <span className="inv-lbl">Terms &amp; Conditions :</span>
                <span className="inv-terms-text">{co.invoiceTerms.split('\n').map((t, i) => <span key={i}>{t}<br /></span>)}</span>
              </td>
            </tr>
          )}
          {hasBank && (
            <tr>
              <td>
                <div className="inv-lbl">Payment Details :</div>
                <div className="inv-pay">
                  <table className="inv-bank">
                    <tbody>
                      {co?.bankName && <tr><td>Bank</td><td>: {co.bankName}{co.bankBranch ? `, ${co.bankBranch}` : ''}</td></tr>}
                      {co?.accountName && <tr><td>Account Name</td><td>: {co.accountName}</td></tr>}
                      {co?.accountNo && <tr><td>Account No</td><td>: {co.accountNo}</td></tr>}
                      {co?.ifsc && <tr><td>IFSC</td><td>: {co.ifsc}</td></tr>}
                      {co?.upiId && <tr><td>UPI ID</td><td>: {co.upiId}</td></tr>}
                    </tbody>
                  </table>
                  {upiLink && (
                    <div className="inv-qr">
                      <QRCodeSVG value={upiLink} size={paper === 'A5' ? 74 : 110} level="M" includeMargin={false} />
                      <div>Scan to pay {money(inv.amount)}</div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export function InvoiceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading, refetch } = useQuery({ queryKey: ['invoice', id], queryFn: () => apiClient.get<any>(`/invoices/${id}`), enabled: !!id })
  const raw: Invoice | undefined = data?.data
  // Older cached responses may predate the tax-inclusive fields; derive them so the sheet never crashes.
  const inv: Invoice | undefined = raw && {
    ...raw,
    lineItems: (raw.lineItems || []).map((l) => ({ ...l, unitLabel: l.unitLabel || (l.unit === 'pkt' ? 'pcs' : l.unit), gstRate: l.gstRate ?? 0, taxAmount: l.taxAmount ?? 0 })),
    collections: raw.collections || [],
    subTotal: raw.subTotal ?? raw.amount,
    cgst: raw.cgst ?? Math.round((raw.gstAmount || 0) * 50) / 100,
    sgst: raw.sgst ?? Math.round(((raw.gstAmount || 0) - Math.round((raw.gstAmount || 0) * 50) / 100) * 100) / 100,
    roundOff: raw.roundOff ?? 0,
    amountInWords: raw.amountInWords || '',
  }

  // ?paper=A5&copies=2 pre-selects the print options (used by print shortcuts and automated print checks).
  const [searchParams] = useSearchParams()
  const [paper, setPaper] = useState<Paper>(searchParams.get('paper') === 'A5' ? 'A5' : 'A4')
  const [copies, setCopies] = useState(() => { const c = Number(searchParams.get('copies')); return c >= 1 && c <= 3 ? c : 3 })
  const [exporting, setExporting] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)

  const [showCollect, setShowCollect] = useState(false)
  const [form, setForm] = useState({ amount: '', date: today(), mode: 'Cash' as Mode, referenceNo: '' })
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const location = useLocation()
  const [message, setMessage] = useState<string>(() => (location.state as any)?.message || '') // e.g. "updated" after an edit

  const [cancelling, setCancelling] = useState(false)

  // Correcting a recorded receipt (part payments, wrong amount / mode / reference). The receipt number is kept.
  const [editRc, setEditRc] = useState<{ id: number; receiptNo: string; amount: string; date: string; mode: Mode; referenceNo: string } | null>(null)
  const [editRcError, setEditRcError] = useState('')
  const [editRcBusy, setEditRcBusy] = useState(false)
  const refreshAfterReceipt = async (msg: string) => {
    setMessage(msg)
    await refetch()
    for (const k of ['stores', 'home-dashboard', 'invoices', 'collections-summary', 'outstanding-bills', 'transactions', 'ledger']) queryClient.invalidateQueries({ queryKey: [k] })
  }
  const saveReceiptEdit = async () => {
    if (!inv || !editRc) return
    const amount = Number(editRc.amount)
    setEditRcError('')
    if (!(amount > 0)) return setEditRcError('Enter an amount greater than 0')
    if (!editRc.date) return setEditRcError('Collection date is required')
    if (editRc.mode === 'Cheque' && !editRc.referenceNo.trim()) return setEditRcError('Cheque number is required')
    setEditRcBusy(true)
    try {
      const res = await apiClient.patch<any>(`/invoices/${id}/collections/${editRc.id}`, { amount, date: editRc.date, mode: editRc.mode, referenceNo: editRc.referenceNo.trim() })
      setEditRc(null)
      await refreshAfterReceipt(res?.message || 'Receipt updated')
    } catch (err: any) {
      setEditRcError(err.response?.data?.message || 'Could not update the receipt')
    } finally {
      setEditRcBusy(false)
    }
  }
  const deleteReceipt = async (c: Collection) => {
    if (!inv || !window.confirm(`Remove ${c.receiptNo} (${formatINR(c.amount)} on ${formatDate(c.date)})? The customer's balance goes back up by that amount.`)) return
    setEditRcBusy(true)
    try {
      const res = await apiClient.delete<any>(`/invoices/${id}/collections/${c.id}`)
      await refreshAfterReceipt(res?.message || 'Receipt removed')
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Could not remove the receipt')
    } finally {
      setEditRcBusy(false)
    }
  }
  const cancelInvoice = async () => {
    if (!inv) return
    if (!window.confirm(`Cancel invoice ${inv.invoiceNo}? The stock goes back and the invoice drops out of balances and reports. The number stays on file as Cancelled.`)) return
    setCancelling(true)
    try {
      const res = await apiClient.post<any>(`/invoices/${id}/cancel`, {})
      setMessage(res.message)
      await refetch()
      for (const k of ['stores', 'home-dashboard', 'invoices', 'stock-overview', 'billing-items', 'collections-summary', 'outstanding-bills']) queryClient.invalidateQueries({ queryKey: [k] })
    } catch (err: any) {
      setMessage('')
      window.alert(err.response?.data?.message || 'Could not cancel the invoice')
    } finally {
      setCancelling(false)
    }
  }

  // Drafts: generate the real invoice (rates must all be filled in) or discard it.
  const [draftBusy, setDraftBusy] = useState(false)
  const generateDraft = async () => {
    if (!inv || !window.confirm(`Generate the invoice from ${inv.invoiceNo}? Stock is allocated, the next invoice number is taken and the sale goes to the ledger.`)) return
    setDraftBusy(true)
    try {
      const res = await apiClient.post<any>(`/invoices/${id}/submit`, {})
      setMessage(res.message)
      await refetch()
      for (const k of ['invoices', 'invoice-drafts', 'invoices-today', 'stores', 'home-dashboard', 'billing-items', 'stock-overview']) queryClient.invalidateQueries({ queryKey: [k] })
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Could not generate the invoice')
    } finally {
      setDraftBusy(false)
    }
  }
  const deleteDraft = async () => {
    if (!inv || !window.confirm(`Delete draft ${inv.invoiceNo}? This cannot be undone.`)) return
    setDraftBusy(true)
    try {
      await apiClient.delete<any>(`/invoices/${id}`)
      for (const k of ['invoices', 'invoice-drafts', 'invoices-today']) queryClient.invalidateQueries({ queryKey: [k] })
      navigate('/dashboard/sales/invoices')
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Could not delete the draft')
      setDraftBusy(false)
    }
  }

  const openCollect = () => { if (inv) setForm({ amount: String(inv.balance), date: today(), mode: 'Cash', referenceNo: '' }); setFormError(''); setShowCollect(true) }
  const submitCollection = async () => {
    setFormError('')
    const amount = Number(form.amount)
    if (!(amount > 0)) return setFormError('Amount must be greater than 0')
    if (inv && amount > inv.balance + 0.005) return setFormError(`Amount exceeds the balance of ${formatINR(inv.balance)}`)
    if (form.mode === 'Cheque' && !form.referenceNo.trim()) return setFormError('Cheque number is required')
    setSaving(true)
    try {
      const res = await apiClient.post<any>(`/invoices/${id}/collections`, { ...form, amount })
      setMessage(res.message)
      setShowCollect(false)
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['stores'] })
      queryClient.invalidateQueries({ queryKey: ['home-dashboard'] })
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Could not record the collection')
    } finally {
      setSaving(false)
    }
  }

  // A sheet that is only a little taller than its page (long address, terms, bank block…) gets tighter spacing so it stays
  // on one page instead of pushing its footer onto a mostly empty second page. Two steps: .inv-compact, then .inv-tight;
  // a really long invoice still flows onto further pages. Measured at the export/print width (718px = 190mm on A4,
  // 506px = 134mm on A5). Returns a function that undoes it.
  const fitSheetsToPage = () => {
    const area = printRef.current
    if (!area) return () => {}
    const pageH = paper === 'A5' ? 732 : 1040
    const hadClass = area.classList.contains('inv-exporting')
    area.classList.add('inv-exporting')
    const touched: HTMLElement[] = []
    area.querySelectorAll<HTMLElement>('.inv-sheet').forEach((sh) => {
      sh.classList.remove('inv-compact', 'inv-tight')
      if (sh.getBoundingClientRect().height <= pageH) return
      touched.push(sh)
      sh.classList.add('inv-compact')
      if (sh.getBoundingClientRect().height > pageH) sh.classList.add('inv-tight')
    })
    if (!hadClass) area.classList.remove('inv-exporting')
    return () => touched.forEach((sh) => sh.classList.remove('inv-compact', 'inv-tight'))
  }

  const downloadPdf = async () => {
    if (!inv || !printRef.current) return
    setExporting(true)
    await new Promise((r) => setTimeout(r, 50))
    const undoFit = fitSheetsToPage()
    try {
      const { default: html2pdf } = await import('html2pdf.js')
      await html2pdf()
        .set({
          margin: paper === 'A5' ? [7, 7, 7, 7] : [10, 10, 10, 10],
          filename: `${inv.invoiceNo}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: paper.toLowerCase(), orientation: 'portrait' },
          // a break after every copy except the last (one after the last would add an empty page at the end)
          pagebreak: { mode: ['css', 'legacy'], after: '.inv-copy-wrap:not(:last-child)' },
        })
        .from(printRef.current)
        .save()
    } finally {
      undoFit()
      setExporting(false)
    }
  }

  // Opens the chat tab straight away (before the async PDF build) so the popup blocker allows it.
  const shareOnWhatsApp = async (link: string) => {
    const tab = window.open('about:blank', '_blank')
    try { await downloadPdf() } catch { /* the chat still opens; the user can use Download PDF */ }
    if (tab) tab.location.href = link
    else window.location.href = link
    setMessage(`${inv?.invoiceNo}.pdf downloaded — in WhatsApp click the paperclip → Document and choose it from your Downloads folder.`)
  }

  const printInvoice = () => {
    const undoFit = fitSheetsToPage()
    window.addEventListener('afterprint', undoFit, { once: true })
    window.print()
  }

  if (isLoading || !inv) return <div className="hdr"><h1>Invoice</h1><p>{isLoading ? 'Loading…' : 'Invoice not found.'}</p></div>

  const copyList = COPY_LABELS.slice(0, copies)

  return (
    <div className="inv-page">
      <style>{`@page { size: ${paper} portrait; margin: 0; }`}</style>
      <div className="hdr print:hidden">
        <div className="field-label-row" style={{ alignItems: 'flex-start' }}>
          <div>
            <h1>{inv.invoiceNo}</h1>
            <p>
              <Link to="/dashboard/sales/invoices" className="text-primary-500 hover:underline">Billing</Link> › <Link to="/dashboard/sales/invoices/list" className="text-primary-500 hover:underline">Invoices</Link> › {inv.invoiceNo} · {inv.channel} sale · generated by {inv.createdBy || '—'}
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-end">
            <span className={`badge ${statusBadge[inv.paymentStatus]}`}>{inv.paymentStatus}{inv.overdueDays > 0 ? ` · ${inv.overdueDays}d overdue` : ''}</span>
            <select value={copies} onChange={(e) => setCopies(Number(e.target.value))} style={{ width: 'auto', marginBottom: 0, padding: '6px 8px', fontSize: 12 }} aria-label="Copies">
              <option value={1}>Original only</option>
              <option value={2}>Original + Duplicate</option>
              <option value={3}>Original + Duplicate + Triplicate</option>
            </select>
            <select value={paper} onChange={(e) => setPaper(e.target.value as Paper)} style={{ width: 'auto', marginBottom: 0, padding: '6px 8px', fontSize: 12 }} aria-label="Paper size">
              <option value="A4">A4</option>
              <option value="A5">A5</option>
            </select>
            <button type="button" className="btn-ghost" onClick={downloadPdf} disabled={exporting}>{exporting ? 'Preparing PDF…' : 'Download PDF'}</button>
            <button type="button" className="btn-ghost" onClick={printInvoice}>Print {paper}</button>
            {inv.editable !== false && inv.paymentStatus !== 'Cancelled' && (
              <button type="button" className="btn-ghost" onClick={() => navigate(`/dashboard/sales/invoices?edit=${inv.id}`)} data-testid="edit-invoice" title="Change the customer, date or items — the invoice number stays the same">Edit</button>
            )}
            {inv.paymentStatus !== 'Draft' && (() => {
              const phone = inv.customerPhone || inv.store?.phone || ''
              const link = waLink(phone, invoiceMessage({ customer: inv.customerName, invoiceNo: inv.invoiceNo, date: inv.invoiceDate, amount: inv.amount, balance: inv.balance, company: inv.company?.name || '', whatsapp: inv.company?.whatsappNumber, upiId: inv.company?.upiId }))
              return link
                ? <a href={link} target="_blank" rel="noopener noreferrer" className="btn-ghost" onClick={(e) => { e.preventDefault(); void shareOnWhatsApp(link) }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} data-testid="wa-invoice" title="Downloads the invoice PDF and opens WhatsApp with the message ready — attach the PDF with the paperclip before sending."><WhatsAppIcon />WhatsApp</a>
                : <button type="button" className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled data-testid="wa-invoice" title="No valid mobile number saved for this customer"><WhatsAppIcon color="#9ca3af" />WhatsApp</button>
            })()}
            {inv.paymentStatus === 'Draft' ? (
              <>
                <button type="button" className="btn-accent" onClick={generateDraft} disabled={draftBusy} data-testid="generate-draft" title="Allocate stock, take the next invoice number and post the sale to the ledger">{draftBusy ? 'Working…' : 'Generate invoice'}</button>
                <button type="button" className="btn-ghost" style={{ color: '#b42318' }} onClick={deleteDraft} disabled={draftBusy} data-testid="delete-draft">Delete draft</button>
              </>
            ) : (
              <>
                {inv.paymentStatus !== 'Cancelled' && <button type="button" className="btn-ghost" style={{ color: '#b42318' }} onClick={cancelInvoice} disabled={cancelling} data-testid="cancel-invoice" title="Cancel this invoice (for example a duplicate) — stock is put back">{cancelling ? 'Cancelling…' : 'Cancel invoice'}</button>}
                {inv.balance > 0 && <button type="button" className="btn-accent" onClick={openCollect}>Record Collection</button>}
              </>
            )}
          </div>
        </div>
      </div>

      {message && <div className="alert alert-success print:hidden">{message}</div>}
      {inv.paymentStatus === 'Draft' && (
        <div className="alert print:hidden" style={{ background: '#FFFBEB', border: '1px solid #E9C46A', color: '#7A4B00' }} data-testid="draft-banner">
          <b>Draft invoice</b> — nothing is in stock or on the customer's ledger yet. Items with no rate show ₹0. Use <b>Edit</b> to fill in the rates, then <b>Generate invoice</b>.
        </div>
      )}

      {showCollect && (
        <div className="card print:hidden" style={{ background: 'var(--primary-soft)' }}>
          <h2>Record Collection — balance {formatINR(inv.balance)}</h2>
          {formError && <div className="alert alert-error">{formError}</div>}
          <div className="row3">
            <div><label>Amount</label><input type="number" min={1} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><label>Date</label><input type="date" value={form.date} max={today()} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <label>Mode</label>
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as Mode })}>
                <option>Cash</option><option>UPI</option><option>Net Banking</option><option>Cheque</option>
              </select>
            </div>
          </div>
          <label>{form.mode === 'Cheque' ? 'Cheque number' : 'Reference'} <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>{form.mode === 'Cheque' ? '' : '— optional'}</small></label>
          <input value={form.referenceNo} onChange={(e) => setForm({ ...form, referenceNo: e.target.value })} placeholder={form.mode === 'UPI' ? 'UPI transaction id' : form.mode === 'Net Banking' ? 'UTR' : ''} />
          <div className="btnrow">
            <button type="button" className="btn-primary" onClick={submitCollection} disabled={saving}>{saving ? 'Saving…' : 'Save collection'}</button>
            <button type="button" className="btn-ghost" onClick={() => setShowCollect(false)}>Cancel</button>
          </div>
          <div className="hint">Posts to Collections and reduces {inv.customerName}'s outstanding credit immediately.</div>
        </div>
      )}

      {/* Printable document: on screen only the ORIGINAL is shown; print / PDF include the selected copies. */}
      <div ref={printRef} className={`inv-print-area ${exporting ? 'inv-exporting' : ''}`}>
        {copyList.map((copy) => (
          <div key={copy} className={`inv-copy-wrap ${paper === 'A5' ? 'a5' : 'a4'}`}>
            <InvoiceSheet inv={inv} copy={copy} paper={paper} />
          </div>
        ))}
      </div>

      <div className={`${inv.store ? 'grid2' : ''} mt-[18px] print:hidden`}>
        <div className="card">
          <div className="field-label-row">
            <h2 style={{ marginBottom: 0 }}>Collections</h2>
            <div className="text-[12.5px]">
              <span className="text-ink-soft">Paid</span> <span className="font-semibold">{formatINR(inv.amountPaid)}</span>
              <span className="text-ink-soft ml-3">Balance</span> <span className={`font-semibold ${inv.balance > 0 ? 'text-danger' : ''}`}>{formatINR(inv.balance)}</span>
            </div>
          </div>
          <div style={{ marginTop: 8 }} />
          {inv.collections.length === 0 && <div className="text-[13px] text-ink-soft">Nothing collected yet{inv.dueDate ? ` — due ${formatDate(inv.dueDate)}` : ''}.</div>}
          {editRcError && <div className="hint-error">{editRcError}</div>}
          {[...inv.collections].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.id - b.id)).map((c) =>
            editRc && editRc.id === c.id ? (
              <div key={c.id} className="py-[9px] border-b border-[#EEEDE7] last:border-b-0 text-[13px]" style={{ background: '#FFFBEB', margin: '0 -8px', padding: '9px 8px' }} data-testid="receipt-edit-row">
                <div className="font-semibold mb-1">{c.receiptNo} <span className="text-ink-soft font-normal text-[11.5px]">— editing</span></div>
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="date" value={editRc.date} onChange={(e) => setEditRc({ ...editRc, date: e.target.value })} className="!w-36 !py-[5px] !text-xs !mb-0" />
                  <select value={editRc.mode} onChange={(e) => setEditRc({ ...editRc, mode: e.target.value as Mode })} className="!w-32 !py-[5px] !text-xs !mb-0">
                    {(['Cash', 'UPI', 'Net Banking', 'Cheque'] as Mode[]).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input type="text" value={editRc.referenceNo} placeholder={editRc.mode === 'Cheque' ? 'cheque no.' : 'UPI / UTR ref'} onChange={(e) => setEditRc({ ...editRc, referenceNo: e.target.value })} className="!w-32 !py-[5px] !text-xs !mb-0" />
                  <input type="number" step="0.01" min={0} value={editRc.amount} onChange={(e) => setEditRc({ ...editRc, amount: e.target.value })} className="!w-28 !py-[5px] !text-xs !mb-0 text-right" />
                  <button type="button" className="btn-accent btn-xs" disabled={editRcBusy} onClick={saveReceiptEdit}>{editRcBusy ? 'Saving…' : 'Save'}</button>
                  <button type="button" className="btn-ghost btn-xs" disabled={editRcBusy} onClick={() => { setEditRc(null); setEditRcError('') }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div key={c.id} className="flex justify-between gap-3 py-[9px] border-b border-[#EEEDE7] last:border-b-0 text-[13px]">
                <div>
                  <div className="font-semibold">{c.receiptNo}</div>
                  <div className="text-ink-soft text-[11.5px]">{formatDate(c.date)} · {c.mode}{c.referenceNo ? ` · ${c.referenceNo}` : ''} · {c.recordedBy}{c.editedBy ? ` · edited by ${c.editedBy}` : ''}</div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="font-semibold">{formatINR(c.amount)}</div>
                  {inv.paymentStatus !== 'Cancelled' && (
                    <div className="mt-[2px]">
                      <button type="button" className="btn-ghost btn-xs" disabled={editRcBusy} onClick={() => { setEditRcError(''); setEditRc({ id: c.id, receiptNo: c.receiptNo, amount: String(c.amount), date: c.date, mode: c.mode, referenceNo: c.referenceNo || '' }) }} title="Correct the amount, date, mode or reference">Edit</button>
                      <button type="button" className="btn-ghost btn-xs ml-1" style={{ color: '#b42318' }} disabled={editRcBusy} onClick={() => deleteReceipt(c)} title="Remove this receipt — the balance goes back up">Remove</button>
                    </div>
                  )}
                </div>
              </div>
            ),
          )}
          {inv.balance > 0 && <button type="button" className="btn-accent btn-sm mt-3" onClick={openCollect}>Record Collection</button>}
        </div>

        {inv.store && (
          <div className="card">
            <h2>{inv.store.name}</h2>
            <span className={`rel-badge ${inv.store.reliabilityGrade === 'New' ? 'B' : inv.store.reliabilityGrade}`}>
              {inv.store.reliabilityGrade === 'New' ? 'New customer' : `Grade ${inv.store.reliabilityGrade} · ${inv.store.onTimePct}% on-time`}
            </span>
            <div className="text-[13px] mt-3 space-y-1">
              <div className="flex justify-between"><span className="text-ink-soft">Outstanding credit</span><span className={inv.store.outstanding > 0 ? 'font-semibold' : ''}>{formatINR(inv.store.outstanding)}</span></div>
              <div className="flex justify-between"><span className="text-ink-soft">Overdue</span><span className={inv.store.overdue > 0 ? 'font-semibold text-danger' : ''}>{formatINR(inv.store.overdue)}</span></div>
              {inv.store.phone && <div className="flex justify-between"><span className="text-ink-soft">Phone</span><span>{inv.store.phone}</span></div>}
            </div>
            <Link to={`/dashboard/sales/invoices/list?store=${inv.store.id}`} className="btn-ghost btn-sm mt-3 inline-block">All invoices for this store</Link>
          </div>
        )}
      </div>
    </div>
  )
}
